import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Skeleton,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import { ConversationAnalysisStyles } from "./ConversationAnalysisStyles";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import {
  IChatMessages,
  IGetConversationAnalysisPayload,
} from "../../models/admin/conversationAnalysis/conversationAnalysis";
import { getConversationAnalysis } from "./ConversatonAnalysisActions";
import { toast, ToastContainer } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

interface IConversationProps {
  selectedSession: IChatMessages | null;
}

const Conversation: React.FC<IConversationProps> = ({ selectedSession }) => {
  const dispatch = useAppDispatch();
  const { conversationAnalysisData, conversations, analysedReport } =
    useAppSelector((state: RootState) => state.conversationAnalysis);

  const { isLoading, calledReducerType, isSuccess } = useAppSelector(
    (state: RootState) => state.common
  );

  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [selectedConversation, setSelectedConversation] = useState<
    IChatMessages[]
  >([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (conversationAnalysisData && selectedSession && conversations) {
      setSelectedConversationId(selectedSession.convoId);
      setSelectedConversation(conversations[selectedSession.convoId] || []);
    } else {
      setSelectedConversationId("");
      setSelectedConversation([]);
    }
  }, [selectedSession, conversations, conversationAnalysisData]);

  useEffect(() => {
    if (isSuccess && calledReducerType === "all/conversationAnalysis") {
      toast.success(analysedReport);
    }
  }, [analysedReport]);

  const handleAnalyze = async (
    conversations: IGetConversationAnalysisPayload
  ) => {
    setIsAnalyzing(true);
    if (conversations) {
      try {
        await dispatch(getConversationAnalysis(conversations));
      } catch (error) {
        toast.error("Failed to Analyze the conversation.");
      } finally {
        setIsAnalyzing(false);
      }
    }
  };

  const handleConversationChange = (e: SelectChangeEvent<string>) => {
    const selectedId = e.target.value as string;
    setSelectedConversationId(selectedId);

    if (conversations) {
      setSelectedConversation(conversations[selectedId] || []);
    } else {
      setSelectedConversation([]);
    }
  };

  return (
    <Box
      sx={ConversationAnalysisStyles.conversationContainer}
      id="conversation_container"
      data-test-id="conversation_container"
      aria-label="Conversation Details"
    >
      <Box
        sx={ConversationAnalysisStyles.conversationHeadingContainer}
        id="conversation_header"
        data-test-id="conversation_header"
        aria-label="Conversation Header"
      >
        <Box
          sx={{
            padding: "0.5rem 1rem",
            display: "flex",
            width: "40%",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
          id="conversation_selector_container"
          data-test-id="conversation_selector_container"
          aria-label="Conversation Selector"
        >
          <Select
            size="small"
            value={selectedConversationId || ""}
            onChange={handleConversationChange}
            displayEmpty
            disabled={!conversationAnalysisData}
            id="conversation_selector"
            data-test-id="conversation_selector"
            aria-label="Select Conversation"
          >
            <MenuItem
              value=""
              disabled
              id="conversation_selector_placeholder"
              data-test-id="conversation_selector_placeholder"
              aria-label="Select a conversation"
            >
              Select a conversation
            </MenuItem>
            {conversations &&
              Object.keys(conversations)?.map((convoId, index) => (
                <MenuItem
                  key={convoId}
                  value={convoId}
                  id={`conversation_option_${convoId}`}
                  data-test-id={`conversation_option_${convoId}`}
                  aria-label={`Conversation ${index + 1}`}
                >
                  {`Conversation ${index + 1}`}
                </MenuItem>
              ))}
          </Select>
        </Box>
        <Button
          sx={{
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
          onClick={() => handleAnalyze(selectedConversation)}
          disabled={!conversationAnalysisData || isAnalyzing}
          id="conversation_analyze_button"
          data-test-id="conversation_analyze_button"
          aria-label="Analyze Conversation"
        >
          {isLoading && calledReducerType === "all/conversationAnalysis" && (
            <CircularProgress color="info" size="14px" />
          )}
          Analyze
        </Button>
      </Box>
      <Divider sx={{ width: "100%" }} />
      <Box
        sx={ConversationAnalysisStyles.chatContainer}
        id="conversation_chat_container"
        data-test-id="conversation_chat_container"
        aria-label="Conversation Chat"
      >
        {isLoading && calledReducerType === "session/conversation" ? (
          <>
            {Array.from(Array(8)).map((_, index) => (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: index % 2 === 0 ? "flex-start" : "flex-end",
                }}
                key={index}
                id={`conversation_skeleton_${index}`}
                data-test-id={`conversation_skeleton_${index}`}
                aria-label="Loading Message"
              >
                <Skeleton
                  variant="rounded"
                  width="45%"
                  height={50}
                  animation="wave"
                />
              </Box>
            ))}
          </>
        ) : conversations && Object.keys(conversations)?.length > 0 ? (
          selectedConversation?.length > 0 ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              id="conversation_messages"
              data-test-id="conversation_messages"
              aria-label="Conversation Messages"
            >
              {selectedConversation?.map((message, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  id={`conversation_message_${index}`}
                  data-test-id={`conversation_message_${index}`}
                  aria-label={`Message ${index + 1}`}
                >
                  <Box
                    sx={{
                      ...ConversationAnalysisStyles.chatMessageContainer,
                      alignSelf: "flex-end",
                    }}
                    id={`conversation_question_${index}`}
                    data-test-id={`conversation_question_${index}`}
                    aria-label={`Question: ${message.question}`}
                  >
                    <Typography
                      id="conversation_question_text"
                      data-test-id="conversation_question_text"
                      aria-label={`Question: ${message.question}`}
                    >
                      {message.question}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      ...ConversationAnalysisStyles.chatMessageContainer,
                      alignSelf: "flex-start",
                      paddingLeft: "1.5rem",
                    }}
                    id={`conversation_answer_${index}`}
                    data-test-id={`conversation_answer_${index}`}
                    aria-label={`Answer: ${message.answer}`}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        table: ({ node, ...props }) => (
                          <table
                            style={{
                              borderCollapse: "collapse",
                              width: "100%",
                              overflow: "scroll",
                            }}
                            {...props}
                          />
                        ),
                        th: ({ node, ...props }) => (
                          <th
                            style={{
                              border: "1px solid #ddd",
                              padding: "8px",
                              backgroundColor: "#f2f2f2",
                              textAlign: "left",
                            }}
                            {...props}
                          />
                        ),
                        td: ({ node, ...props }) => (
                          <td
                            style={{
                              minWidth: "150px",
                              border: "1px solid #ddd",
                              padding: "8px",
                            }}
                            {...props}
                          />
                        ),
                      }}
                    >
                      {message.answer}
                    </ReactMarkdown>
                    <Box
                      sx={ConversationAnalysisStyles.feedBackIconContainer}
                      id={`conversation_feedback_${index}`}
                      data-test-id={`conversation_feedback_${index}`}
                      aria-label={`Feedback: ${
                        message.feedback === "P"
                          ? "Positive"
                          : message.feedback === "N"
                          ? "Negative"
                          : "None"
                      }`}
                    >
                      {message.feedback === "P" ? (
                        <ThumbUpAltOutlinedIcon sx={{ color: "green" }} />
                      ) : message.feedback === "N" ? (
                        <ThumbDownAltOutlinedIcon sx={{ color: "orange" }} />
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              sx={{ textAlign: "center" }}
              fontWeight="bold"
              id="conversation_no_selection"
              data-test-id="conversation_no_selection"
              aria-label="No Conversation Selected"
            >
              No conversation selected. Please select a conversation.
            </Typography>
          )
        ) : (
          <Typography
            sx={{ textAlign: "center" }}
            fontWeight="bold"
            id="conversation_no_sessions"
            data-test-id="conversation_no_sessions"
            aria-label="No Sessions Available"
          >
            Click on a session to see their conversations.
          </Typography>
        )}
      </Box>
      <ToastContainer
        position="bottom-right"
        autoClose={10000}
        icon={false}
        toastClassName="custom-toast"
      />
    </Box>
  );
};

export default Conversation;
